// Runtime shortcut registry. Screens register handlers for their bindings on mount,
// unregister on unmount. The global key listener (mounted once in <App />) resolves
// keystrokes against the active keymap → looks up the handler → invokes.

import { create } from 'zustand';
import type { Keymap, ShortcutBinding } from './types';
import { tallyKeymap } from './tallyKeymap';
import { modernKeymap } from './modernKeymap';

export type Handler = () => void;

interface RegistryState {
  mode: 'tally' | 'modern';
  /** Map of binding-id → handler (handlers replace each other; only one active per id). */
  handlers: Map<string, Handler>;
  /** Currently active scope. Bindings with scope === '*' always match; otherwise must equal. */
  scope: string;

  setMode: (mode: 'tally' | 'modern') => void;
  setScope: (scope: string) => void;
  registerHandler: (id: string, handler: Handler) => () => void;
  resolve: (combo: string) => ShortcutBinding | null;
  getKeymap: () => Keymap;
  invoke: (id: string) => boolean;
}

export const useShortcutRegistry = create<RegistryState>((set, get) => ({
  mode: 'tally',
  handlers: new Map(),
  scope: '*',

  setMode: (mode) => set({ mode }),
  setScope: (scope) => set({ scope }),

  registerHandler: (id, handler) => {
    const next = new Map(get().handlers);
    next.set(id, handler);
    set({ handlers: next });
    return () => {
      const cleanup = new Map(get().handlers);
      // Only remove if the registered handler is still ours (avoid clearing a replacement)
      if (cleanup.get(id) === handler) {
        cleanup.delete(id);
        set({ handlers: cleanup });
      }
    };
  },

  getKeymap: () => {
    return get().mode === 'tally' ? tallyKeymap : modernKeymap;
  },

  resolve: (combo) => {
    const keymap = get().getKeymap();
    const scope = get().scope;
    for (const b of keymap) {
      if (b.scope && b.scope !== '*' && b.scope !== scope) continue;
      if (b.combos.includes(combo)) return b;
    }
    return null;
  },

  invoke: (id) => {
    const handler = get().handlers.get(id);
    if (handler) {
      handler();
      return true;
    }
    return false;
  },
}));
